import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { computeFloatingPosition } from "../lib/position";
import type { FloatingSide } from "../lib/position";

export type TooltipSide = FloatingSide;

interface TriggerProps extends HTMLAttributes<HTMLElement> {
  "aria-describedby"?: string;
}

export interface TooltipProps {
  /** Conteúdo curto. Tooltip não guarda informação essencial. */
  content: ReactNode;
  /** Gatilho: um único elemento focável (botão, link, span tabIndex={0}). */
  children: ReactElement<TriggerProps>;
  /** Lado PREFERIDO. Perto da borda o balão troca de lado sozinho. */
  side?: TooltipSide;
  /** Atraso de abertura no hover (ms). Foco e toque abrem na hora. */
  delay?: number;
  /** Largura máxima do balão. */
  className?: string;
  disabled?: boolean;
  /** Força aberto (vitrine, onboarding guiado). Ignora hover/foco. */
  open?: boolean;
}

/**
 * Tooltip sem biblioteca: `role="tooltip"` + `aria-describedby` no gatilho, abre no hover,
 * no FOCO (teclado) e no TOQUE (celular não tem hover); fecha no ESC, ao tirar o foco e ao
 * tocar fora.
 *
 * I6 — colisão: o balão sai num portal com `position: fixed` e as coordenadas vêm de
 * `computeFloatingPosition` (troca de lado quando não cabe e empurra para dentro da
 * janela). Por estar no portal, nenhum `overflow: hidden` de tabela ou card corta o balão.
 * Rolagem e redimensionamento recalculam a posição enquanto ele está aberto.
 */
export function Tooltip({
  content,
  children,
  side = "top",
  delay = 150,
  className,
  disabled = false,
  open: forcedOpen,
}: TooltipProps) {
  const id = useId();
  const [hoverOpen, setOpen] = useState(false);
  const open = !disabled && (forcedOpen ?? hoverOpen);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  /** O toque também dispara `focus`; isto evita o abre-e-fecha na mesma batida. */
  const touchedAt = useRef(0);
  const [position, setPosition] = useState<{ top: number; left: number; side: TooltipSide } | null>(
    null,
  );

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const show = useCallback(
    (immediate: boolean) => {
      if (disabled) return;
      clear();
      if (immediate || delay === 0) setOpen(true);
      else timer.current = setTimeout(() => setOpen(true), delay);
    },
    [clear, delay, disabled],
  );

  const hide = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble || typeof window === "undefined") return;
    const a = anchor.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    setPosition(
      computeFloatingPosition({
        anchor: { top: a.top, left: a.left, width: a.width, height: a.height },
        floating: { width: b.width, height: b.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        side,
      }),
    );
  }, [side]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    place();
  }, [open, place, content]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") hide();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || bubbleRef.current?.contains(target)) return;
      hide();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, hide, place]);

  if (!isValidElement(children)) return children;

  const trigger = cloneElement(children, {
    "aria-describedby": open ? id : children.props["aria-describedby"],
    onMouseEnter: (event: ReactMouseEvent<HTMLElement>) => {
      children.props.onMouseEnter?.(event);
      show(false);
    },
    onMouseLeave: (event: ReactMouseEvent<HTMLElement>) => {
      children.props.onMouseLeave?.(event);
      hide();
    },
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      children.props.onFocus?.(event);
      if (Date.now() - touchedAt.current < 600) return;
      show(true);
    },
    onBlur: (event: ReactFocusEvent<HTMLElement>) => {
      children.props.onBlur?.(event);
      hide();
    },
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
      children.props.onPointerDown?.(event);
      if (event.pointerType !== "touch" || disabled) return;
      touchedAt.current = Date.now();
      clear();
      setOpen((value) => !value);
    },
  });

  const style: CSSProperties = position
    ? { top: position.top, left: position.left }
    : { top: 0, left: 0, visibility: "hidden" };

  // Sempre no DOM (escondido quando fechado): o conteúdo continua legível para teste e
  // para quem inspeciona, e o `aria-describedby` aponta para algo que já existe.
  const bubble = (
    <span
      ref={bubbleRef}
      id={id}
      role="tooltip"
      hidden={!open}
      data-side={position?.side ?? side}
      style={style}
      className={cn(
        // bolha INVERSA (tinta sobre o painel, painel sobre a tinta no escuro)
        "pointer-events-none fixed z-50 w-max max-w-65 rounded-md bg-surface-inverse px-2.5 py-2 text-left",
        "text-xs font-normal leading-snug text-inverse-foreground shadow-md",
        className,
      )}
    >
      {content}
    </span>
  );

  return (
    <span ref={anchorRef} className="relative inline-flex">
      {trigger}
      {typeof document !== "undefined" ? createPortal(bubble, document.body) : bubble}
    </span>
  );
}
